import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Clock, AlertCircle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_aestha-hub/artifacts/58pjy83g_IMG_0746.jpeg";

export const ExamTake = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    fetchExam();
  }, [examId]);

  useEffect(() => {
    if (exam?.time_limit_minutes && timeLeft === null) {
      setTimeLeft(exam.time_limit_minutes * 60);
    }
  }, [exam]);

  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;
    
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const fetchExam = async () => {
    try {
      const response = await axios.get(`${API}/exams/${examId}`);
      setExam(response.data);
    } catch (error) {
      toast.error("Erro ao carregar simulado");
      navigate("/exams");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, optionId) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: optionId,
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setShowConfirm(false);

    try {
      const formattedAnswers = Object.entries(answers).map(([questionId, optionId]) => ({
        question_id: questionId,
        selected_option_id: optionId,
      }));

      const response = await axios.post(`${API}/exams/${examId}/submit`, {
        answers: formattedAnswers,
      });

      toast.success("Simulado finalizado!");
      navigate(`/exams/${examId}/result/${response.data.id}`);
    } catch (error) {
      toast.error("Erro ao enviar respostas");
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground font-mono text-sm uppercase tracking-widest">
          Carregando...
        </div>
      </div>
    );
  }

  if (!exam) return null;

  const question = exam.questions[currentQuestion];
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / exam.questions.length) * 100;

  return (
    <div className="min-h-screen bg-white" data-testid="exam-take-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b z-50">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/exams")}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            data-testid="back-button"
          >
            <ArrowLeft size={18} />
            <span className="hidden sm:inline">Sair</span>
          </button>

          <img src={LOGO_URL} alt="AESTHA" className="h-6 invert" />

          {timeLeft !== null && (
            <div className={`flex items-center gap-2 font-mono text-sm ${
              timeLeft < 60 ? "text-red-600" : "text-muted-foreground"
            }`}>
              <Clock size={16} />
              {formatTime(timeLeft)}
            </div>
          )}
        </div>
        
        {/* Progress bar */}
        <div className="progress-bar">
          <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
        </div>
      </header>

      {/* Question */}
      <main className="pt-24 pb-32 px-4">
        <div className="question-container animate-slideUp">
          {/* Question counter */}
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground text-center mb-8">
            Questão {currentQuestion + 1} de {exam.questions.length}
          </p>

          {/* Question text */}
          <h2 className="question-text" data-testid="question-text">
            {question.text}
          </h2>

          {/* Options */}
          <div className="space-y-3" data-testid="options-container">
            {question.options.map((option, index) => {
              const letters = ["A", "B", "C", "D", "E"];
              const isSelected = answers[question.id] === option.id;

              return (
                <button
                  key={option.id}
                  onClick={() => handleAnswer(question.id, option.id)}
                  className={`option-button ${isSelected ? "selected" : ""}`}
                  data-testid={`option-${index}`}
                >
                  <span className="option-letter">{letters[index]}</span>
                  <span className="flex-1 text-left">{option.text}</span>
                </button>
              );
            })}
          </div>
        </div>
      </main>

      {/* Footer Navigation */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentQuestion((prev) => prev - 1)}
            disabled={currentQuestion === 0}
            className="btn-secondary"
            data-testid="prev-button"
          >
            <ArrowLeft size={16} className="mr-2" />
            Anterior
          </Button>

          <p className="font-mono text-xs text-muted-foreground">
            {answeredCount}/{exam.questions.length} respondidas
          </p>

          {currentQuestion < exam.questions.length - 1 ? (
            <Button
              onClick={() => setCurrentQuestion((prev) => prev + 1)}
              className="btn-primary"
              data-testid="next-button"
            >
              Próxima
              <ArrowRight size={16} className="ml-2" />
            </Button>
          ) : (
            <Button
              onClick={() => setShowConfirm(true)}
              className="btn-primary"
              disabled={submitting}
              data-testid="finish-button"
            >
              Finalizar
            </Button>
          )}
        </div>
      </footer>

      {/* Confirm Dialog */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-xl">
              Finalizar simulado?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Você respondeu {answeredCount} de {exam.questions.length} questões.
              {answeredCount < exam.questions.length && (
                <span className="block mt-2 text-yellow-600">
                  <AlertCircle className="inline mr-1" size={14} />
                  Existem questões não respondidas!
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary">Voltar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSubmit}
              className="btn-primary"
              data-testid="confirm-finish"
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ExamTake;
